import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { SettingOutlined } from '@ant-design/icons';
import PickUpDisplaySettingModal from '@components/common/form/PickUpDisplaySettingModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DATE_FORMAT_VALUE, DEFAULT_TABLE_ITEM_SIZE } from '@constants';
import apiConfig from '@constants/apiConfig';
import { orderDisplayLayouts } from '@constants/masterData';
import useListBase from '@hooks/useListBase';
import useTranslate from '@hooks/useTranslate';
import { BaseTable, BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { convertUtcToLocalTime, orderNumber } from '@utils';
import { Button } from 'antd';

const RestaurantPickUpDisplayLivePage = () => {
    const { restaurantId } = useParams();
    const translate = useTranslate();

    const [openSettingModal, setOpenSettingModal] = useState(false);
    const [selectedSettingRecord, setSelectedSettingRecord] = useState(null);
        
    const handleOpenSetting = (record) => {
        setSelectedSettingRecord(record);
        setOpenSettingModal(true);
    };
        
    const handleCloseSettingModal = () => {
        setOpenSettingModal(false);
        setSelectedSettingRecord(null);
    };
    

    const { data, mixinFuncs, loading, pagination, queryFilter } = useListBase({
        apiConfig: {
            ...apiConfig.orderDisplay,
            getList: apiConfig.orderDisplay.getListCustomer,
        },
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(commonMessage.pickUpDisplay),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.prepareGetListParams = (filter) => {
                const page = parseInt(queryFilter.page);
                return {
                    ...filter,
                    restaurantId,
                    page: page > 0 ? page - 1 : 0,
                    size: DEFAULT_TABLE_ITEM_SIZE,
                };
            };
            funcs.additionalActionColumnButtons = () => ({
                editSetting: (record) => (
                    <BaseTooltip title={translate.formatMessage(commonMessage.editSetting)}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenSetting(record);
                            }}
                        >
                            <SettingOutlined />
                        </Button>
                    </BaseTooltip>
                ),
            });
        },
    });

    const columns = [
        {
            title: '#',
            dataIndex: 'index',
            width: 60,
            align: 'center',
            render: (text, record, index) => orderNumber(pagination, index),
        },
        {
            title: translate.formatMessage(commonMessage.deviceId),
            dataIndex: [ 'device', 'deviceId' ],
            width: 160,
        },
        {
            title: translate.formatMessage(commonMessage.deviceName),
            dataIndex: [ 'device', 'name' ],
        },
        {
            title: translate.formatMessage(commonMessage.layout),
            dataIndex: 'layout',
            width: 140,
            render: (layout) => {
                const option = orderDisplayLayouts.find((item) => item.value === layout);
                return option ? translate.formatMessage(option.label) : null;
            },
        },
        {
            title: translate.formatMessage(commonMessage.expireDate),
            dataIndex: [ 'device', 'expireDate' ],
            width: 180,
            align: 'right',
            render: (expireDate) =>
                expireDate ? convertUtcToLocalTime(expireDate, DATE_FORMAT_VALUE, DATE_FORMAT_VALUE) : null,
        },
        mixinFuncs.renderStatusColumn({ width: 120, dataIndex: [ 'device', 'status' ] }),
        mixinFuncs.renderActionColumn({ editSetting: { ignorePermission: true } }, { width: 120 }),
    ].filter(Boolean);

    return (
        <PageWrapper routes={[ { breadcrumbName: translate.formatMessage(commonMessage.pickUpDisplay) } ]}>
            <ListPage
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        pagination={pagination}
                        loading={loading}
                        rowKey="id"
                    />
                }
            />
            <PickUpDisplaySettingModal
                open={openSettingModal}
                onClose={handleCloseSettingModal}
                selectedRecord={selectedSettingRecord}
                restaurantId={restaurantId} 
                onSaveSuccess={() => mixinFuncs.getList()} 
            />
        </PageWrapper>
    );
};

export default RestaurantPickUpDisplayLivePage;
